import React, { useEffect, useRef } from 'react';
import { LayoutDashboard, Settings, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui';
import SOSButton from './SOSButton';

const Navigation = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = useRef(null);
  const isClient = user?.role === 'client';

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMenuOpen(false);
  };

  const handleSettings = () => {
    navigate('/settings');
    setMenuOpen(false);
  };

  const handleDashboard = () => {
    const target = user?.role === 'admin' ? '/admin' : '/dashboard';
    navigate(target);
    setMenuOpen(false);
  };

  return (
    <nav className="bg-white border-b border-neutral-200 shadow-soft" role="navigation" aria-label="Main navigation">
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <img 
            src="/logo with words.png" 
            alt="MHDF Momentum" 
            className="h-10 w-auto cursor-pointer"
            onClick={handleDashboard}
          />
        </div>

        {user && (
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={handleDashboard}>
              Dashboard
            </Button>
            {isClient && (
              <Button variant="ghost" onClick={() => navigate('/client-resources')}>
                Resources
              </Button>
            )}
            {isClient && (
              <Button variant="secondary" onClick={() => navigate('/dashboard#homework-hub')}>
                Homework Hub
              </Button>
            )}

            {/* SOS Crisis Support Button - Universal across all roles */}
            <SOSButton variant="navigation" />

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((prev) => !prev)}
                className="flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-sm font-medium text-neutral-700 shadow-soft transition-gentle hover:border-neutral-300"
              >
                <span className="hidden text-neutral-500 sm:block">
                  {user.fullName || `${user.firstName} ${user.lastName}`}
                </span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold text-white">
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </span>
              </button>

              {menuOpen && (
                <div className="absolute right-0 mt-3 w-48 rounded-soft-lg border border-neutral-100 bg-white p-2 shadow-soft-lg">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={handleDashboard}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start gap-2"
                    onClick={handleSettings}
                  >
                    <Settings className="h-4 w-4" />
                    Settings
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full justify-start gap-2 text-error-600 hover:text-error-700"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
